/**
 * @file `Learners` — namespacer for `admin.learners.*` corresponding
 *       to the `/learners` panel in the admin shell.
 * @module @labcraft/admin/src/client/learners
 *
 * One `CoreLifecycle`-based namespacer per UI panel (per the workspace
 * Client-shape rule in `.agents/rules/services.md`). Owns no transport
 * of its own — the parent `Admin` adopts the shared `HttpTransport`
 * and hands a reference here at construction. Logs and metrics bubble
 * under `admin.learners.*`.
 */

import { CoreLifecycle } from '@labcraft/core'
import type { HttpTransport } from './index.ts'
import {
  AttachTagRoute,
  DetachTagRoute,
  LearnersListRoute,
  ListAllTagsRoute,
  ListLearnerTagsRoute,
  SetLearnerStatusRoute,
  UpdateLearnerProfileRoute,
  type Learner,
  type LearnerListFilters,
  type LearnersListResponse,
  type LearnerStatus,
  type LearnerSortField,
  type LearnerSortDir,
  type UpdateLearnerProfileRequest,
} from '../shared/routes.ts'

/**
 * Filter for `Learners.list`. The composable {@link LearnerListFilters}
 * subset (status, free-text, signup-date range, timezone, acquisition
 * source/category, progress signal — all compose as AND) plus the
 * offset-paging knobs. All optional; the daemon applies a sensible
 * default page size (100) when `limit` is absent. The Emails compose
 * surface uses the richer filters to scope a broadcast's candidate list;
 * the Learners panel still uses only `status` + `q`.
 *
 * @property limit - Page size, 1..200. Default 100.
 * @property offset - Rows to skip before this page (default 0). Drives
 *   the numbered pager: page N requests `offset = (N - 1) * limit`.
 * @property sort - Sort field. Absent → the daemon's status-derived
 *   default (inactive → oldest signup first; else most-recent login).
 * @property dir - Sort direction. Requires `sort`.
 */
export type LearnersListArgs = LearnerListFilters & {
  limit?: number
  offset?: number
  sort?: LearnerSortField
  dir?: LearnerSortDir
}

/**
 * Read + write namespacer for the Learners panel. Held on the
 * frontend `Admin` Client; React components reach it via
 * `useClient<Admin>().learners`.
 */
export class Learners extends CoreLifecycle<{ id?: string }> {
  private readonly http: HttpTransport

  constructor(http: HttpTransport) {
    super({ id: 'learners' })
    this.http = http
  }

  /**
   * Fetch one page of learners.
   *
   * @param args - Filter / paging options.
   * @returns The page plus the total count across all pages.
   */
  async list(args: LearnersListArgs = {}): Promise<LearnersListResponse> {
    const query: Record<string, string | number | undefined> = {
      status: args.status,
      q: args.q,
      signedUpAfter: args.signedUpAfter,
      signedUpBefore: args.signedUpBefore,
      timezone: args.timezone,
      acquisitionSource: args.acquisitionSource,
      acquisitionCategory: args.acquisitionCategory,
      hasProgress:
        args.hasProgress === undefined ? undefined : String(args.hasProgress),
      // Tag include/exclude travel as comma-joined GET scalars (the typed
      // transport's query args are scalar-only); the admin daemon splits them
      // back into arrays and the account daemon canonicalizes each token.
      tags: args.tags?.length ? args.tags.join(',') : undefined,
      tagsNot: args.tagsNot?.length ? args.tagsNot.join(',') : undefined,
      limit: args.limit,
      offset: args.offset,
      sort: args.sort,
      dir: args.dir,
    }
    return this.http.request(LearnersListRoute, { query })
  }

  /**
   * Flip a learner's access state. Returns the updated row so the
   * caller can replace the cached version without a follow-up GET.
   *
   * @param id - Learner UUID.
   * @param status - Target access state.
   */
  async setStatus(id: string, status: LearnerStatus): Promise<Learner> {
    const res = await this.http.request(SetLearnerStatusRoute, {
      params: { id },
      body: { status },
    })
    return res.learner
  }

  /**
   * Operator partial edit of a learner's profile (#1639) — `display_name` /
   * `timezone` / `avatar_url`. `email` is read-only and not part of the
   * patch. Returns the updated row so the drawer re-renders without a
   * follow-up read.
   *
   * @param id - Learner UUID.
   * @param patch - The partial profile edit (only present fields are written).
   * @returns The updated learner row.
   */
  async updateProfile(
    id: string,
    patch: UpdateLearnerProfileRequest,
  ): Promise<Learner> {
    const res = await this.http.request(UpdateLearnerProfileRoute, {
      params: { id },
      body: patch,
    })
    return res.learner
  }

  /**
   * Attach a tag to a learner (#1639). The tag is forwarded raw and
   * canonicalized by the account daemon. Returns the learner's tag set after
   * the attach so the drawer can re-render its chips without a follow-up GET.
   *
   * @param id - Learner UUID.
   * @param tag - The tag to attach.
   * @returns The learner's tags after the attach (alphabetical).
   */
  async attachTag(id: string, tag: string): Promise<string[]> {
    const res = await this.http.request(AttachTagRoute, {
      params: { id },
      body: { tag },
    })
    return res.tags
  }

  /**
   * Detach a tag from a learner (#1639). Returns the learner's tag set after
   * the detach.
   *
   * @param id - Learner UUID.
   * @param tag - The tag to detach.
   * @returns The learner's tags after the detach (alphabetical).
   */
  async detachTag(id: string, tag: string): Promise<string[]> {
    const res = await this.http.request(DetachTagRoute, {
      params: { id, tag },
    })
    return res.tags
  }

  /**
   * List one learner's tags (#1639), alphabetical. The drawer's
   * authoritative single-learner read, refreshed on open + after mutations.
   *
   * @param id - Learner UUID.
   * @returns The learner's tags (empty when untagged).
   */
  async listTags(id: string): Promise<string[]> {
    const res = await this.http.request(ListLearnerTagsRoute, {
      params: { id },
    })
    return res.tags
  }

  /**
   * List every distinct tag across all learners (#1639), alphabetical. Backs
   * the drawer + filter autocomplete over existing tags.
   *
   * @returns Every distinct tag (alphabetical).
   */
  async listAllTags(): Promise<string[]> {
    const res = await this.http.request(ListAllTagsRoute)
    return res.tags
  }
}
